import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, getUser } from "@/lib/api";
import Shell from "@/components/Shell";
import { FileDigit, Plus, Lock, Loader2, FolderOpen, Pencil, Check, X } from "lucide-react";

function Stamp({ status }) {
    if (status === "locked")
        return (
            <span className="classified-stamp" data-testid="status-stamp-locked">
                SEALED
            </span>
        );
    return (
        <span
            className="border border-[#FFB000] text-[#FFB000] px-2 py-0.5 text-[10px] font-accent tracking-[0.25em] uppercase"
            data-testid="status-stamp-draft"
        >
            DRAFT
        </span>
    );
}

export default function Dashboard() {
    const [dossiers, setDossiers] = useState(null);
    const [showNew, setShowNew] = useState(false);
    // Iter 31.172 — inline rename on the list page (internal, non-locked).
    const [renamingId, setRenamingId] = useState(null);
    const [nameDraft, setNameDraft] = useState("");
    const [busyRename, setBusyRename] = useState(false);
    const user = getUser();
    const nav = useNavigate();

    const load = async () => {
        const { data } = await api.get("/dossiers");
        setDossiers(data);
    };
    useEffect(() => {
        load();
    }, []);

    const startRename = (e, d) => {
        e.preventDefault();
        e.stopPropagation();
        setRenamingId(d.id);
        setNameDraft(d.name || "");
    };

    const saveRename = async (e, d) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        const next = nameDraft.trim();
        if (!next || next === d.name) { setRenamingId(null); return; }
        setBusyRename(true);
        try {
            await api.patch(`/dossiers/${d.id}`, { name: next });
            await load();
            setRenamingId(null);
        } catch (err) {
            alert(err?.response?.data?.detail || "Rename failed.");
        } finally {
            setBusyRename(false);
        }
    };

    const cancelRename = (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        setRenamingId(null);
    };

    return (
        <Shell>
            <div className="flex items-end justify-between mb-10">
                <div>
                    <div className="label-typed tw-prefix">Case Registry · Finance Ops</div>
                    <h1 className="font-heading text-5xl text-ink tracking-[0.04em] uppercase mt-1">
                        Dossier Vault<span className="cursor-blink" />
                    </h1>
                    <p className="font-body text-sm text-ink-soft mt-3">
                        {dossiers ? `${dossiers.length} case file(s) open on the desk.` : "Unlocking the cabinet..."}
                    </p>
                </div>
                {user?.role === "internal" && (
                    <button
                        onClick={() => setShowNew(true)}
                        className="inline-flex items-center gap-2 bg-ink text-paper-light hover:bg-stamp-red px-6 py-3 font-heading uppercase tracking-[0.25em] text-xs"
                        data-testid="create-dossier-button"
                    >
                        <Plus className="w-4 h-4" strokeWidth={2} /> New Dossier
                    </button>
                )}
            </div>

            {showNew && <NewDossierModal onClose={() => setShowNew(false)} onCreated={(d) => { setShowNew(false); nav(`/dossiers/${d.id}`); }} />}

            {dossiers === null ? (
                <div className="flex items-center gap-3 text-ink-soft font-heading text-sm uppercase tracking-[0.3em]">
                    <Loader2 className="w-4 h-4 animate-spin" /> Unsealing the archive...
                </div>
            ) : dossiers.length === 0 ? (
                <div
                    className="paper-card border border-dashed border-ink/30 p-16 text-center tilt-l"
                    data-testid="dossiers-empty-state"
                >
                    <FolderOpen className="w-10 h-10 text-ink mx-auto mb-4" strokeWidth={1} />
                    <div className="font-heading text-3xl uppercase tracking-[0.06em] text-ink-faint">
                        Archive empty
                    </div>
                    <div className="label-typed mt-2">
                        {user?.role === "internal" ? "Commission a new file above." : "Awaiting assignment from Control."}
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="dossiers-grid">
                    {dossiers.map((d) => (
                        <Link
                            key={d.id}
                            to={`/dossiers/${d.id}`}
                            className={`block paper-card tape-corner p-6 hover:border-stamp-red transition-colors group ${d.status === 'locked' ? 'tilt-r' : 'tilt-l'}`}
                            data-testid={`dossier-card-${d.code}`}
                        >
                            <div className="flex items-start justify-between mb-4 gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="label-typed">{d.code}</div>
                                    {renamingId === d.id ? (
                                        <div className="flex items-center gap-1 mt-1" onClick={(e) => e.preventDefault()}>
                                            <input
                                                type="text"
                                                value={nameDraft}
                                                onChange={(e) => { e.stopPropagation(); setNameDraft(e.target.value); }}
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                onKeyDown={(e) => {
                                                    e.stopPropagation();
                                                    if (e.key === "Enter") saveRename(e, d);
                                                    if (e.key === "Escape") cancelRename(e);
                                                }}
                                                autoFocus
                                                data-testid={`dossier-rename-input-${d.code}`}
                                                className="bg-paper border-b-2 border-stamp-red font-heading text-lg uppercase px-1 py-0 outline-none flex-1 min-w-0"
                                            />
                                            <button
                                                onClick={(e) => saveRename(e, d)}
                                                disabled={busyRename || !nameDraft.trim()}
                                                className="text-stamp-red hover:opacity-70 disabled:opacity-30"
                                                data-testid={`dossier-rename-save-${d.code}`}
                                                title="Save"
                                            >
                                                {busyRename ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                            </button>
                                            <button
                                                onClick={cancelRename}
                                                className="text-ink-soft hover:text-ink"
                                                data-testid={`dossier-rename-cancel-${d.code}`}
                                                title="Cancel"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="font-heading text-2xl uppercase tracking-[0.04em] text-ink group-hover:text-stamp-red mt-1 inline-flex items-baseline gap-2">
                                            {d.name}
                                            {user?.role === "internal" && d.status !== "locked" && (
                                                <button
                                                    onClick={(e) => startRename(e, d)}
                                                    className="text-ink-soft hover:text-stamp-red text-base opacity-0 group-hover:opacity-100 transition-opacity"
                                                    data-testid={`dossier-rename-${d.code}`}
                                                    title="Rename"
                                                >
                                                    <Pencil className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <Stamp status={d.status} />
                            </div>
                            <div className="hairline mb-4" />
                            <div className="grid grid-cols-2 gap-3 font-body text-xs">
                                <div>
                                    <div className="label-typed">Entity</div>
                                    <div className="text-ink">{d.entity}</div>
                                </div>
                                <div>
                                    <div className="label-typed">Period</div>
                                    <div className="text-ink">
                                        {d.period_start?.slice(0, 10)} → {d.period_end?.slice(0, 10)}
                                    </div>
                                </div>
                                <div>
                                    <div className="label-typed">Opening £</div>
                                    <div className="text-stamp-red font-heading">£{Number(d.opening_balance).toFixed(2)}</div>
                                </div>
                                <div>
                                    <div className="label-typed">Reserve £</div>
                                    <div className="text-ink">£{Number(d.reserve_threshold).toFixed(2)}</div>
                                </div>
                            </div>
                            <div className="mt-4 flex items-center gap-2 label-typed">
                                {d.status === "locked" ? <Lock className="w-3 h-3" /> : <FileDigit className="w-3 h-3" />}
                                {d.status === "locked" ? "Sealed — read only" : "Open for adjustment"}
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </Shell>
    );
}

function NewDossierModal({ onClose, onCreated }) {
    const today = new Date().toISOString().slice(0, 10);
    const past = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
    const [form, setForm] = useState({
        name: "Spy Academy — Operations Review",
        entity: "Spy Academy",
        period_start: past,
        period_end: today,
        opening_balance: 25000,
        reserve_threshold: 5000,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            const { data } = await api.post("/dossiers", form);
            onCreated(data);
        } catch (e) {
            setErr(e.response?.data?.detail?.toString() || "Creation failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-40 bg-black/80 flex items-center justify-center p-4" data-testid="new-dossier-modal">
            <form
                onSubmit={submit}
                className="border border-[#333] bg-[#0a0a0a] max-w-lg w-full p-8 grain"
            >
                <div className="font-accent text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-2">
                    // Commission New Case
                </div>
                <div className="font-heading text-3xl uppercase tracking-widest mb-6">
                    Open Dossier
                </div>
                {["name", "entity"].map((k) => (
                    <div key={k} className="mb-4">
                        <label className="label-typed block mb-1">
                            {k}
                        </label>
                        <input
                            value={form[k]}
                            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                            required
                            className="w-full px-3 py-2 text-sm"
                            data-testid={`new-dossier-${k}`}
                        />
                    </div>
                ))}
                <div className="grid grid-cols-2 gap-4">
                    {["period_start", "period_end"].map((k) => (
                        <div key={k}>
                            <label className="label-typed block mb-1">
                                {k.replace("_", " ")}
                            </label>
                            <input
                                type="date"
                                value={form[k]}
                                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                required
                                className="w-full px-3 py-2 text-sm"
                                data-testid={`new-dossier-${k}`}
                            />
                        </div>
                    ))}
                    {["opening_balance", "reserve_threshold"].map((k) => (
                        <div key={k}>
                            <label className="label-typed block mb-1">
                                {k.replace("_", " ")} £
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={form[k]}
                                onChange={(e) => setForm({ ...form, [k]: parseFloat(e.target.value) || 0 })}
                                required
                                className="w-full px-3 py-2 text-sm"
                                data-testid={`new-dossier-${k}`}
                            />
                        </div>
                    ))}
                </div>
                {err && (
                    <div className="mt-4 text-stamp-red label-typed" data-testid="new-dossier-error">
                        {err}
                    </div>
                )}
                <div className="flex gap-3 mt-6">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 border border-ink/30 px-6 py-2 font-heading uppercase text-xs tracking-[0.25em] hover:border-stamp-red hover:text-stamp-red"
                        data-testid="new-dossier-cancel"
                    >
                        Abort
                    </button>
                    <button
                        type="submit"
                        disabled={busy}
                        className="flex-1 bg-ink text-paper-light hover:bg-stamp-red px-6 py-2 font-heading uppercase text-xs tracking-[0.25em] disabled:opacity-50 inline-flex items-center justify-center gap-2"
                        data-testid="new-dossier-submit"
                    >
                        {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                        Commission
                    </button>
                </div>
            </form>
        </div>
    );
}
